import Vue from 'vue'
import VueRouter from 'vue-router'
import {RawLocation, Route} from 'vue-router/types/router'

import MyPage from '../component/mypage.vue'
import Regist from '../component/register/regist.vue'
import RegistConfirm from '../component/register/regist-confirm.vue'
import RegistComplete from '../component/register/regist-complete.vue'
import MailConfirmWaiting from '../component/mail-confirm-waiting.vue'
import Error from '../component/error.vue'
import Logout from '../component/logout.vue'
import Callback from '../component/callback.vue'
import {NavigationGuardUtils} from "@/navigation-guard-utils";
import {UserInfoForRegisterTmpModel} from "@/model/user-info/user-info-for-register-tmp.model";
import {ReservationModel} from "@/model/reservation.model";
import PolicyBase from '../component/policy/policy-base.vue';
import {Settings} from 'luxon';
import CarInfoSelect from "@/external/car-info-select/car-info-select.vue";
import {GtmRouterNext} from "@/router/gtm-router-next";
import {GtmScript} from "@/router/gtm-script";

// GTMのスクリプトをロードする
let gtm = new GtmScript();
gtm.loadScript(process.env.VUE_APP_GTM_ID);

Vue.use(VueRouter);

/**
 * ルートガード
 * 認証チェックのみを行います。beforeEnterで指定して下さい。
 */
let checkAuth = (to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void => {
    NavigationGuardUtils.checkAuth(to, from, new GtmRouterNext(gtm, to, from, next), (next: GtmRouterNext) => {
            next.next();
        },
        "redirectUrl",
        "displayMode",
        "selectedPage");
};

/**
 * ルートガード(メールアドレス確認待ち画面用)
 * 引数情報のセッション登録、認証チェックを行います。beforeEnterで指定して下さい。
 */
let checkAuthForMailConfirm = (to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void => {
    let gtmNext = new GtmRouterNext(gtm, to, from, next);

    if ((to.query.mailPending) && (to.query.mailPending == "true")) {
        // Mail検証未の場合（callbackからの遷移）、 Auth0認証は済みなので、処理を継続
        gtmNext.next();
    } else {
        // 他サービスからの直接のアクセスの場合、認証をチェックし、処理を継続
        NavigationGuardUtils.checkAuth(to, from, gtmNext, () => {
                next();
            },
            "redirectUrlForMail",
            "displayMode",
            "selectedPage");
    }
};

/**
 * ルートガード(会員情報登録画面用)
 * 引数情報のセッション登録、認証チェックを行います。beforeEnterで指定して下さい。
 */
let checkAuthForRegist = (to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void => {
    // クエリパラメータをセッションに保持
    if (Object.keys(to.query).length != 0) {
        // 会員情報
        let member: UserInfoForRegisterTmpModel = new UserInfoForRegisterTmpModel();
        member.user.name = to.query.name ? to.query.name.toString() : null;
        member.user.tel = to.query.tel ? to.query.tel.toString() : null;

        // 予約連携
        let reservation: ReservationModel = new ReservationModel;
        reservation.reservationId = to.query.reservationId ? to.query.reservationId.toString() : null;
        reservation.reservationKey = to.query.reservationKey ? to.query.reservationKey.toString() : null;

        // セッションにデータを設定
        sessionStorage.setItem("memberQuery", JSON.stringify(member));
        sessionStorage.setItem("reservationQuery", JSON.stringify(reservation));
    }

    // 認証チェックを実行する
    checkAuth(to, from, next);
};

/**
 * ルート ガード
 * 認証チェックと会員情報チェックを行います。beforeEnterで指定して下さい。
 */
let checkAuthAndUser = (to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void => {
    // 認証チェック後（/auth）、会員情報をチェックする（/userinfo）
    NavigationGuardUtils.checkAuth(to, from, new GtmRouterNext(gtm, to, from, next), NavigationGuardUtils.checkUserInfo, "redirectUrl", "displayMode", "selectedPage");
};

/**
 * ルート ガード
 * 認証チェックと会員情報チェックを行います。beforeEnterで指定して下さい。
 * 以下のクエリパラメータの値をsessionStorageで保持します。
 * <ul>
 *     <li>showCustomerCode</li>
 * </ul>
 */
let checkAuthWithEnsureQueryParameters = (to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void => {
    if ((to.query.showCustomerCode) && (to.query.showCustomerCode == "true")) {
        sessionStorage.setItem("showCustomerCode", to.query.showCustomerCode);
    }
    NavigationGuardUtils.checkAuth(to, from, new GtmRouterNext(gtm, to, from, next), NavigationGuardUtils.checkUserInfo,
        "redirectUrl",
        "displayMode",
        "selectedPage");
};

Settings.defaultLocale = 'ja';

/** ルートの設定。共通認証チェック、会員情報チェックはbeforeEnterで設定してください。 */
const routes = [
    {path: '/', name: 'root', component: MyPage, beforeEnter: checkAuthAndUser},
    {path: '/client/mypage', name: 'mypage', component: MyPage, beforeEnter: checkAuthAndUser},
    {path: '/client/customer-code', name: 'mypage', component: MyPage, beforeEnter: checkAuthWithEnsureQueryParameters},
    {path: '/client/regist', name: 'regist', component: Regist, ignoreAuth: true, props: true, beforeEnter: checkAuthForRegist},
    {path: '/client/regist-confirm', name: 'regist-confirm', component: RegistConfirm, ignoreAuth: true, props: true, beforeEnter: checkAuth},
    {path: '/client/regist-complete', name: 'regist-complete', component: RegistComplete, beforeEnter: checkAuthAndUser},
    {path: '/client/mail-confirm-waiting', name: 'mail-confirm-waiting', component: MailConfirmWaiting, beforeEnter: checkAuthForMailConfirm},
    {path: '/client/policy/:kind', name: 'policy', component: PolicyBase},
    {path: '/client/error', name: 'error', component: Error},
    {path: '/client/logout', name: 'logout', component: Logout},
    {path: '/client/callback', name: 'callback', component: Callback},
    {path: '/client/car-info-switching', name: 'car-info-switching', component: CarInfoSelect, beforeEnter: checkAuthAndUser},
];

const router = new VueRouter({
    mode: 'history',
    routes
});

export default router
